import { Vector3 } from "three";
import { Vector2 } from "three";
import { Triangle } from "three";
import { CorePoint } from "./Point";
import { CoreMath } from "../math/_Module";
import { ArrayUtils } from "../ArrayUtils";
export class CoreFace {
  constructor(_coreGeometry, _index) {
    this._coreGeometry = _coreGeometry;
    this._index = _index;
    this._geometry = this._coreGeometry.geometry();
  }
  index() {
    return this._index;
  }
  points() {
    return this._points = this._points || this._get_points();
  }
  applyMatrix4(matrix) {
    for (let point of this.points()) {
      point.applyMatrix4(matrix);
    }
  }
  _get_points() {
    var _a;
    const indexArray = ((_a = this._geometry.index) == null ? void 0 : _a.array) || [];
    const start = this._index * 3;
    return [
      new CorePoint(this._coreGeometry, indexArray[start + 0]),
      new CorePoint(this._coreGeometry, indexArray[start + 1]),
      new CorePoint(this._coreGeometry, indexArray[start + 2])
    ];
  }
  positions() {
    return this._positions = this._positions || this._getPositions();
  }
  _getPositions() {
    const points = this.points();
    return [points[0].position(), points[1].position(), points[2].position()];
  }
  triangle() {
    return this._triangle = this._triangle || this._get_triangle();
  }
  _get_triangle() {
    const positions = this.positions();
    return new Triangle(positions[0], positions[1], positions[2]);
  }
  deltas() {
    return this._deltas = this._deltas || this._getDeltas();
  }
  _getDeltas() {
    const positions = this.positions();
    return [positions[1].clone().sub(positions[0]), positions[2].clone().sub(positions[0])];
  }
  area() {
    return this.triangle().getArea();
  }
  center(target) {
    const positions = this.positions();
    target.x = (positions[0].x + positions[1].x + positions[2].x) / 3;
    target.y = (positions[0].y + positions[1].y + positions[2].y) / 3;
    target.z = (positions[0].z + positions[1].z + positions[2].z) / 3;
    return target;
  }
  randomPosition(seed) {
    let weights = [CoreMath.randFloat(seed), CoreMath.randFloat(seed * 6541)];
    if (weights[0] + weights[1] > 1) {
      weights[0] = 1 - weights[0];
      weights[1] = 1 - weights[1];
    }
    return this.positions()[0].clone().add(this.deltas()[0].clone().multiplyScalar(weights[0])).add(this.deltas()[1].clone().multiplyScalar(weights[1]));
  }
  attribValueAtPosition(attrib_name, position) {
    const barycentricCoordinates = new Vector3();
    this.triangle().getBarycoord(position, barycentricCoordinates);
    const weights = barycentricCoordinates.toArray();
    const attrib = this._geometry.attributes[attrib_name];
    const attribSize = attrib.itemSize;
    const pointValues = this.points().map((point) => point.attribValue(attrib_name));
    let newAttribValue;
    let sum;
    let index = 0;
    switch (attribSize) {
      case 1: {
        sum = 0;
        for (let pointValue of pointValues) {
          sum += pointValue * weights[index];
          index++;
        }
        newAttribValue = sum;
        break;
      }
      default: {
        for (let pointValue of pointValues) {
          const weightedValue = pointValue.multiplyScalar(weights[index]);
          if (sum) {
            sum.add(weightedValue);
          } else {
            sum = weightedValue;
          }
          index++;
        }
        newAttribValue = sum;
      }
    }
    return newAttribValue;
  }
  static interpolatedValue(geometry, face, intersectPoint, attrib) {
    const pointIndices = [face.a, face.b, face.c];
    const positionAttrib = geometry.getAttribute("position");
    const positionAttribArray = positionAttrib.array;
    const pointPositions = pointIndices.map((point_index) => new Vector3(positionAttribArray[point_index * 3 + 0], positionAttribArray[point_index * 3 + 1], positionAttribArray[point_index * 3 + 2]));
    const attribSize = attrib.itemSize;
    const attribArray = attrib.array;
    let attribValues = [];
    switch (attribSize) {
      case 1:
        attribValues = pointIndices.map((point_index) => attribArray[point_index]);
        break;
      case 2:
        attribValues = pointIndices.map((point_index) => new Vector2(attribArray[point_index * 2 + 0], attribArray[point_index * 2 + 1]));
        break;
      case 3:
        attribValues = pointIndices.map((point_index) => new Vector3(attribArray[point_index * 3 + 0], attribArray[point_index * 3 + 1], attribArray[point_index * 3 + 2]));
        break;
    }
    const distToPoints = pointIndices.map((point_index, i) => intersectPoint.distanceTo(pointPositions[i]));
    const distanceTotal = ArrayUtils.sum([
      distToPoints[0] * distToPoints[1],
      distToPoints[0] * distToPoints[2],
      distToPoints[1] * distToPoints[2]
    ]);
    const weights = [
      distToPoints[1] * distToPoints[2] / distanceTotal,
      distToPoints[0] * distToPoints[2] / distanceTotal,
      distToPoints[0] * distToPoints[1] / distanceTotal
    ];
    let newAttribValue;
    switch (attribSize) {
      case 1:
        newAttribValue = ArrayUtils.sum(pointIndices.map((point_indx, i) => weights[i] * attribValues[i]));
        break;
      default:
        var values = pointIndices.map((point_index, i) => attribValues[i].multiplyScalar(weights[i]));
        newAttribValue = null;
        for (let value of values) {
          if (newAttribValue) {
            newAttribValue.add(value);
          } else {
            newAttribValue = value;
          }
        }
    }
    return newAttribValue;
  }
}
